// PlanModal — AI Plan & Apply confirmation modal.
//
// Spec: docs/specs/ai-plan-and-apply.md §6.
//
// State machine (§11):
//
//   preview  → user clicks「套用」    → applying
//   applying → all ops finished       → results
//   results  → close OR retry failed  → preview (with filtered ops) / done
//
// Dispatcher (planExecutor below) walks operations sequentially. Each op
// fires an existing write endpoint with the user's cookie; every request
// carries `x-plan-correlation-id` so the resulting audit rows share a
// group id. First failure stops the batch (subsequent ops marked
// skipped). No auto-rollback — successful prior ops persist (spec §7.2).

import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Loader2, MinusCircle, X } from "lucide-react";
import type { PlanProposal, PlanOperation } from "../lib/chatApi.js";
import { createAssignment, patchAssignment } from "../lib/assignmentsApi.js";
import { createLocation, patchLocation } from "../lib/locationsApi.js";
import { createRoute, patchRoute, putRouteLocations } from "../lib/routesApi.js";
import { createTask, patchTask } from "../lib/tasksApi.js";
import { createClient, patchClient } from "../lib/clientsApi.js";
import { createSite, patchSite } from "../lib/sitesApi.js";
import { createBuilding, patchBuilding, createFloor, patchFloor } from "../lib/floorStackApi.js";
import { createShift, patchShift } from "../lib/shiftsApi.js";
import { createRoster, patchRoster } from "../lib/rosterApi.js";
import { putOfficerSites } from "../lib/usersApi.js";
import {
  createRole,
  updateRole,
  replaceRolePermissions,
  deleteRole,
  getRole,
  getUserRoles,
  putUserRoles,
} from "../lib/rolesApi.js";

export type OpResult =
  | { kind: "pending" }
  | { kind: "running" }
  | { kind: "success"; durationMs: number; created_id?: string }
  | { kind: "fail"; errorCode: string; message: string; httpStatus?: number }
  | { kind: "skipped"; reason: "previous_failed" };

export type PlanPhase =
  | { kind: "preview" }
  | { kind: "applying"; currentIdx: number }
  | { kind: "results" };

export interface PlanModalProps {
  plan: PlanProposal;
  tenantId: string | null;
  /** Called after the user closes the modal — with the final results so
   *  the caller can post a tool_result back to the AI so it can
   *  acknowledge. Always fires exactly once per modal lifecycle. */
  onClose: (finalResults: OpResult[]) => void;
}

/** PlanModal — backdrop + centered card wrapper around PlanPanel.
 *  Used by pro-mode floating ChatBubble where Plan & Apply needs to
 *  hover over the rest of the dashboard. AI-mode AI 導覽 page uses
 *  PlanPanel directly inside the drawer instead.
 *
 *  Backdrop dismissal routes through PlanPanel's `attemptClose`
 *  imperative handle so the apply-phase guard isn't bypassed (clicking
 *  the backdrop while ops are dispatching would leave the queue
 *  half-run; PlanPanel ignores the close call in that phase). */
export default function PlanModal(props: PlanModalProps): React.ReactElement {
  const { plan } = props;
  const panelRef = useRef<PlanPanelHandle>(null);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={plan.title}
      className="fixed inset-0 z-[9100] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) panelRef.current?.attemptClose();
      }}
    >
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-bg-surface rounded-xl shadow-2xl border border-border overflow-hidden">
        <PlanPanel ref={panelRef} {...props} />
      </div>
    </div>
  );
}

export interface PlanPanelHandle {
  /** Same phase-guarded close as the X button. Hosts that need to
   *  dismiss the panel from outside (PlanModal backdrop, future
   *  drawer X) route through here so an in-flight apply isn't
   *  interrupted mid-dispatch. */
  attemptClose: () => void;
}

/** PlanPanel — the inner card content (Header / op list / Footer) +
 *  the state machine. Renders as a fragment whose immediate children
 *  use `flex-shrink-0` (header / footer) + `flex-1 min-h-0` (op list)
 *  so the parent flex column drives the scroll. Parent supplies the
 *  outer flex-col container + width / height / chrome.
 *
 *  Used as the body of PlanModal (centered floating card) AND
 *  directly inside the AI 導覽 drawer in AM-5b. */
export const PlanPanel = forwardRef<PlanPanelHandle, PlanModalProps>(function PlanPanel(
  { plan, tenantId, onClose }, ref,
): React.ReactElement {
  const [phase, setPhase] = useState<PlanPhase>({ kind: "preview" });
  const [results, setResults] = useState<OpResult[]>(() =>
    plan.operations.map(() => ({ kind: "pending" as const })),
  );
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  // High-stakes acknowledgment (spec custom-rbac.md §7.3 — delete_role +
  // revoke_role_from_user 等不可逆操作須先勾「我了解」). Gates the「套用」
  // button when ANY op in the plan is high-stakes.
  const hasHighStakes = useMemo(() => plan.operations.some(isHighStakesOp), [plan.operations]);
  const [ackChecked, setAckChecked] = useState(false);

  const hasPreflight = Array.isArray(plan.preflight_errors) && plan.preflight_errors.length > 0;
  const preflightByOp = useMemo(() => {
    const map = new Map<number, Array<{ field: string; reason: string; detail?: string }>>();
    if (!plan.preflight_errors) return map;
    for (const e of plan.preflight_errors) {
      const arr = map.get(e.op_index) ?? [];
      arr.push({ field: e.field, reason: e.reason, detail: e.detail });
      map.set(e.op_index, arr);
    }
    return map;
  }, [plan.preflight_errors]);

  // Counters for the header. Derived so they always reflect latest state.
  const counts = useMemo(() => {
    let success = 0, fail = 0, skipped = 0, pending = 0;
    for (const r of results) {
      if (r.kind === "success") success++;
      else if (r.kind === "fail") fail++;
      else if (r.kind === "skipped") skipped++;
      else pending++;
    }
    return { success, fail, skipped, pending };
  }, [results]);

  const applyPlan = async (): Promise<void> => {
    setPhase({ kind: "applying", currentIdx: 0 });
    const next: OpResult[] = plan.operations.map(() => ({ kind: "pending" as const }));
    // `prevResults` records each successful op's server-assigned id so
    // later ops in the SAME plan can $ref it (spec §5.11). Local to
    // this apply invocation — no state leakage across plans.
    const prevResults = new Map<number, { id: string }>();
    let stopped = false;
    for (let i = 0; i < plan.operations.length; i++) {
      if (stopped) {
        next[i] = { kind: "skipped", reason: "previous_failed" };
        continue;
      }
      next[i] = { kind: "running" };
      setResults([...next]);
      setPhase({ kind: "applying", currentIdx: i });
      const start = performance.now();
      try {
        // Resolve any $ref placeholders against prevResults BEFORE
        // dispatch. Throws `unresolved_reference` if a referenced op
        // didn't run / failed / produced no id.
        const resolved = resolveOpRefs(plan.operations[i], i, prevResults);
        const createdId = await dispatchOperation(resolved, plan.plan_correlation_id, tenantId);
        next[i] = {
          kind: "success",
          durationMs: Math.round(performance.now() - start),
          ...(createdId ? { created_id: createdId } : {}),
        };
        if (createdId) prevResults.set(i, { id: createdId });
      } catch (e) {
        const err = e as { code?: string; message?: string; upstreamMessage?: string; status?: number };
        // Server may include a human-readable `message` alongside the `error`
        // code (e.g. roster overlap returns 「已有重疊的班別：早班 (06:00-14:00)」).
        // chatApi wrappers expose it as `.upstreamMessage`; fall back to Error
        // .message (which defaults to the code) so the OpResult always carries
        // *some* user-meaningful text.
        next[i] = {
          kind: "fail",
          errorCode: err.code ?? "request_failed",
          message: err.upstreamMessage ?? err.message ?? "unknown_error",
          httpStatus: err.status,
        };
        stopped = true;
      }
      setResults([...next]);
    }
    setPhase({ kind: "results" });
  };

  const close = (): void => {
    // Refuse close during apply — leaves the dispatcher half-run.
    if (phase.kind === "applying") return;
    onClose(results);
  };

  useImperativeHandle(ref, () => ({ attemptClose: close }), [phase, results]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Header */}
      <div className="flex items-start gap-3 p-4 border-b border-border flex-shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-text-primary truncate">{plan.title}</h2>
          {plan.summary && phase.kind === "preview" && (
            <p className="mt-1 text-xs text-text-secondary leading-relaxed">{plan.summary}</p>
          )}
          {phase.kind === "applying" && (
            <p className="mt-1 text-xs text-accent">
              套用中... ({phase.currentIdx + 1} / {plan.operations.length})
            </p>
          )}
          {phase.kind === "results" && (
            <p className="mt-1 text-xs text-text-secondary">
              完成 {counts.success} / {plan.operations.length}
              {counts.fail > 0 && <span className="text-danger"> · {counts.fail} 失敗</span>}
              {counts.skipped > 0 && <span className="text-text-muted"> · {counts.skipped} 未執行</span>}
            </p>
          )}
        </div>
        {phase.kind !== "applying" && (
          <button
            type="button"
            onClick={close}
            aria-label="關閉"
            className="text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Op list */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
        {hasPreflight && phase.kind === "preview" && (
          <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2.5 text-xs text-danger flex items-start gap-2 mb-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">系統預檢發現 {plan.preflight_errors!.length} 個問題</p>
              <p className="mt-0.5 text-danger/70">以下標紅的操作引用了不存在或已停用的資源。請關閉此方案、請 AI 修正後重新規劃。</p>
            </div>
          </div>
        )}
        {plan.operations.map((op, idx) => (
          <OpCard
            key={idx}
            idx={idx}
            op={op}
            result={results[idx]}
            expanded={expandedIdx === idx}
            onToggleExpand={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            isCurrent={phase.kind === "applying" && phase.currentIdx === idx}
            preflightErrors={preflightByOp.get(idx)}
          />
        ))}
      </div>

      {/* High-stakes acknowledgment — only shown in preview when ANY op in
          the plan is irreversible (delete_role / revoke_role_from_user).
          Spec custom-rbac.md §7.3. */}
      {phase.kind === "preview" && hasHighStakes && (
        <div className="px-4 py-3 border-t border-danger/30 bg-danger/5 flex-shrink-0">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ackChecked}
              onChange={(e) => setAckChecked(e.target.checked)}
              className="mt-0.5 rounded border-border bg-bg-base text-danger focus:ring-danger focus:ring-offset-0"
            />
            <span className="text-xs text-danger leading-relaxed">
              <AlertCircle className="inline-block h-3.5 w-3.5 mr-1 -mt-0.5" />
              我了解此方案包含不可逆的操作（刪除角色 / 撤銷使用者角色），套用後無法復原。
            </span>
          </label>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end gap-2 p-4 border-t border-border bg-bg-raised flex-shrink-0">
        {phase.kind === "preview" && (
          <>
            <button type="button" onClick={close} className="btn-secondary text-sm">取消</button>
            <button
              type="button"
              onClick={() => void applyPlan()}
              disabled={hasPreflight || (hasHighStakes && !ackChecked)}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="h-4 w-4" /> {hasPreflight ? "預檢未通過" : `套用 ${plan.operations.length} 筆`}
            </button>
          </>
        )}
        {phase.kind === "applying" && (
          <span className="text-xs text-text-muted inline-flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> 套用中無法取消
          </span>
        )}
        {phase.kind === "results" && (
          <button type="button" onClick={close} className="btn-primary text-sm">關閉</button>
        )}
      </div>
    </>
  );
});

function OpCard({
  idx, op, result, expanded, onToggleExpand, isCurrent, preflightErrors,
}: {
  idx: number;
  op: PlanOperation;
  result: OpResult;
  expanded: boolean;
  onToggleExpand: () => void;
  isCurrent: boolean;
  preflightErrors?: Array<{ field: string; reason: string; detail?: string }>;
}): React.ReactElement {
  const statusIcon = ((): React.ReactElement => {
    switch (result.kind) {
      case "pending":   return <span className="text-text-muted text-2xs font-mono">{idx + 1}.</span>;
      case "running":   return <Loader2 className="h-4 w-4 text-accent animate-spin" />;
      case "success":   return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "fail":      return <AlertCircle className="h-4 w-4 text-danger" />;
      case "skipped":   return <MinusCircle className="h-4 w-4 text-text-muted" />;
    }
  })();
  return (
    <div className={`rounded-lg border ${preflightErrors?.length ? "border-danger/60" : isCurrent ? "border-accent" : "border-border"} bg-bg-raised`}>
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-start gap-2 p-3 text-left"
      >
        <span className="mt-0.5 flex-shrink-0 w-5 inline-flex items-center justify-center">{statusIcon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-text-primary truncate">{op.display}</p>
          <p className="mt-0.5 text-2xs text-text-muted truncate">{opKindLabel(op.kind)}</p>
          {result.kind === "fail" && (
            <p className="mt-1 text-2xs text-danger break-words">
              錯誤：{result.errorCode}{result.message && result.errorCode !== result.message ? `（${result.message}）` : ""}
            </p>
          )}
          {preflightErrors && preflightErrors.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {preflightErrors.map((e, j) => (
                <p key={j} className="text-2xs text-danger break-words">
                  ⚠ {e.field}：{e.reason === "not_found" ? "找不到" : e.reason === "inactive" ? "已停用" : "重複"}{e.detail ? `（${e.detail}）` : ""}
                </p>
              ))}
            </div>
          )}
          {result.kind === "skipped" && (
            <p className="mt-1 text-2xs text-text-muted">（因前項失敗未執行）</p>
          )}
        </div>
        <span className="flex-shrink-0 mt-0.5 text-text-muted">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-border-subtle pt-2 space-y-2">
          {/* Both patch and create ops render human-friendly per-field
              detail (FIELD_LABELS + Chinese values). No raw JSON / no
              ULID payload — parent references (client_id / site_id /
              etc.) are implied by the op's `display` text at the card
              header; surfacing them here would only add engineer noise. */}
          {op.kind === "assign_officer_sites"
            ? <AssignOfficerSitesDiffView op={op} />
            : isPatchOp(op) || isUpdateRoleOp(op)
              ? <PatchDiffView op={op as Extract<PlanOperation, { kind: `patch_${string}` }>} />
              : <CreateDetailView op={op} />}
        </div>
      )}
    </div>
  );
}

function isPatchOp(op: PlanOperation): op is Extract<PlanOperation, { kind: `patch_${string}` }> {
  return op.kind.startsWith("patch_");
}

/** Phase 6 update_role has the same before/changes diff shape as patch_*.
 *  Routes through PatchDiffView. */
function isUpdateRoleOp(op: PlanOperation): op is Extract<PlanOperation, { kind: "update_role" }> {
  return op.kind === "update_role";
}

/** Phase 6 high-stakes ops — delete_role + revoke_role_from_user — require
 *  the operator to tick an explicit acknowledgment before「套用」 enables. */
function isHighStakesOp(op: PlanOperation): boolean {
  return op.kind === "delete_role" || op.kind === "revoke_role_from_user";
}

// ─── Inter-op $ref resolution (spec §5.11) ───────────────────────────
//
// AI can emit `{ "$op_idx": N, "$path": "id" }` placeholders in any
// identifier field; dispatcher replaces them with the actual id from
// an earlier op's result before issuing the API call. Backward-only
// (op N can only ref idx < N); forward / self ref throw early.

interface OpRefShape {
  $op_idx: number;
  $path: "id";
}

function isOpRefValue(v: unknown): v is OpRefShape {
  return (
    typeof v === "object" && v !== null &&
    typeof (v as { $op_idx?: unknown }).$op_idx === "number" &&
    (v as { $path?: unknown }).$path === "id"
  );
}

/** Custom Error class so applyPlan's catch can shape it the same way
 *  as server errors (`{ code, message, status? }`). */
class UnresolvedReferenceError extends Error {
  code = "unresolved_reference";
  status = 0;
  constructor(message: string) {
    super(message);
    this.name = "UnresolvedReferenceError";
  }
}

/** Deep-clone `op` while replacing any `$ref` placeholder with the
 *  literal id from `prevResults`. Throws UnresolvedReferenceError on:
 *   - forward / self ref ($op_idx >= currentIdx)
 *   - parent op didn't produce a result (failed / skipped / patch-only)
 *  Spec §5.11. */
function resolveOpRefs<T extends PlanOperation>(
  op: T,
  currentIdx: number,
  prevResults: Map<number, { id: string }>,
): T {
  function walk(value: unknown): unknown {
    if (isOpRefValue(value)) {
      const idx = value.$op_idx;
      if (idx >= currentIdx) {
        throw new UnresolvedReferenceError(`forward or self ref: $op_idx=${idx} is not before current ${currentIdx}`);
      }
      const r = prevResults.get(idx);
      if (!r) {
        throw new UnresolvedReferenceError(`op ${idx} produced no id (failed / skipped / patch-only)`);
      }
      return r.id;
    }
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) out[k] = walk(v);
      return out;
    }
    return value;
  }
  return walk(op) as T;
}

/** Narrow an IdRef-typed value to its literal id, post-resolveOpRefs.
 *  Defensive — if a $ref slipped through (shouldn't happen) it throws
 *  the same error class so the dispatcher catch surfaces it cleanly. */
function idOf(v: unknown): string {
  if (typeof v === "string") return v;
  if (isOpRefValue(v)) {
    throw new UnresolvedReferenceError(`unresolved $ref reached dispatcher: $op_idx=${v.$op_idx}`);
  }
  throw new UnresolvedReferenceError(`expected id string, got ${typeof v}`);
}

/** Same as idOf but allows null (for nullable identifier columns like
 *  route_id, location_hint, lat). */
function idOfOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  return idOf(v);
}

/** Identifier ULIDs (Crockford base32, 26 chars). When a value matches
 *  this pattern AND the field name is a known parent-reference key, we
 *  treat it as engineering noise and either hide (create detail) or
 *  shorten to「結尾 XXXX」 (patch diff). The shortened form is enough
 *  for operators to correlate against audit logs without flooding the
 *  modal with raw ULIDs. */
const ULID_IDENTIFIER_FIELDS = new Set([
  "task_id",
  "location_id",
  "route_id",
  "site_id",
  "client_id",
  "building_id",
  "floor_id",
  "assignment_id",
]);
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;
function isUlidLike(v: unknown): v is string {
  return typeof v === "string" && ULID_RE.test(v);
}

/** CreateDetailView — expanded view for kind=create_* ops. Renders the
 *  op's payload as a key-value list using FIELD_LABELS + renderDiffValue.
 *  Parent ULIDs (client_id / site_id / building_id …) are hidden — the
 *  card header's `display` text is the human-readable trust anchor. */
function CreateDetailView({ op }: { op: PlanOperation }): React.ReactElement {
  const rows = buildCreateRows(op);
  return rows.length === 0 ? (
    <p className="text-2xs text-text-muted italic">（無額外欄位）</p>
  ) : (
    <div className="space-y-1">
      {rows.map((row) => (
        <div key={row.field} className="flex flex-wrap items-baseline gap-x-1.5 text-2xs text-text-secondary">
          <span className="font-medium">{FIELD_LABELS[row.field] ?? row.field}：</span>
          <span className="text-text-primary break-all">{row.rendered}</span>
        </div>
      ))}
    </div>
  );
}

interface CreateRow { field: string; rendered: string }

function buildCreateRows(op: PlanOperation): CreateRow[] {
  const out: CreateRow[] = [];
  for (const [field, value] of Object.entries(op as Record<string, unknown>)) {
    if (field === "kind" || field === "display") continue;
    // `before` carries diff snapshot metadata — surfaced via patchEntityLabel
    // / high-stakes warnings, not as a row in the field detail list.
    if (field === "before") continue;
    if (value === undefined || value === null) continue;
    // Inter-op $ref placeholder — surface explicitly even on parent-id
    // fields, so operator knows「這格會自動拿前面那個 op 的結果」.
    // ULID skip below would otherwise hide it. Spec §5.11.
    if (ULID_IDENTIFIER_FIELDS.has(field) && isOpRefValue(value)) {
      out.push({ field, rendered: renderDiffValue(field, value) });
      continue;
    }
    // Skip parent-reference ULIDs — operator sees parent in `display`.
    if (ULID_IDENTIFIER_FIELDS.has(field) && isUlidLike(value)) continue;
    // Skip target_value when it's an officer ULID (target_kind=officer).
    // For target_kind=role, target_value is a role string and renders fine.
    if (field === "target_value" && isUlidLike(value)) {
      out.push({ field, rendered: `特定巡邏員（識別碼 結尾 ${value.slice(-4)}）` });
      continue;
    }
    // create_route.locations — array of {location_id, expected_dwell_seconds}.
    // We render a count (operators verify count + order via display text);
    // listing each ULID would be useless noise.
    if (field === "locations" && Array.isArray(value)) {
      out.push({ field: "locations", rendered: `${value.length} 個地點（依此順序加入新路線）` });
      continue;
    }
    // Phase 6 — permission_ids array: render as count (operators verify the
    // full list in the role detail page after apply). Long ULID lists are
    // unreadable in the modal card.
    if (field === "permission_ids" && Array.isArray(value)) {
      out.push({ field: "permission_ids", rendered: `${value.length} 個權限` });
      continue;
    }
    // Phase 6 — scope object: render as zh-TW summary instead of raw JSON.
    if (field === "scope" && typeof value === "object" && value !== null) {
      const scope = value as { sites?: unknown; buildings?: unknown; clients?: unknown };
      const parts: string[] = [];
      if (Array.isArray(scope.sites) && scope.sites.length) parts.push(`案場 ${scope.sites.length} 個`);
      if (Array.isArray(scope.buildings) && scope.buildings.length) parts.push(`樓棟 ${scope.buildings.length} 個`);
      if (Array.isArray(scope.clients) && scope.clients.length) parts.push(`客戶 ${scope.clients.length} 個`);
      out.push({ field: "scope", rendered: parts.length === 0 ? "整個單位（tenant-wide）" : `限定：${parts.join("、")}` });
      continue;
    }
    out.push({ field, rendered: renderDiffValue(field, value) });
  }
  return out;
}

/** assign_officer_sites — REPLACE semantics, so render「修改前 → 修改後」
 *  by diffing the before.site_names array against the proposed site_ids
 *  count. Site ULIDs alone aren't meaningful to operators, but the
 *  before names give context;「+N 個」/「-N 個」 deltas give the rest. */
function AssignOfficerSitesDiffView({ op }: { op: Extract<PlanOperation, { kind: "assign_officer_sites" }> }): React.ReactElement {
  const entityLabel = patchEntityLabel(op);
  const beforeNames = op.before.site_names;
  const beforeCount = beforeNames.length;
  const afterCount = op.site_ids.length;
  return (
    <div className="space-y-2">
      {entityLabel && (
        <p className="text-2xs text-text-secondary font-medium">{entityLabel}</p>
      )}
      <div className="text-2xs text-text-secondary">
        <div className="flex items-baseline gap-1.5">
          <span className="font-medium">案場數：</span>
          <span className="text-text-muted line-through">{beforeCount}</span>
          <span className="text-text-muted">→</span>
          <span className="text-text-primary font-medium">{afterCount}</span>
        </div>
        {beforeNames.length > 0 && (
          <div className="mt-1 flex flex-wrap items-baseline gap-1.5">
            <span className="font-medium">原案場：</span>
            <span className="text-text-muted">{beforeNames.join("、")}</span>
          </div>
        )}
        {afterCount === 0 && (
          <p className="mt-1 text-2xs text-warning">⚠ 此操作將清除該執勤者的所有案場指派</p>
        )}
      </div>
    </div>
  );
}

function PatchDiffView({ op }: { op: Extract<PlanOperation, { kind: `patch_${string}` }> }): React.ReactElement {
  const before = op.before as unknown as Record<string, unknown>;
  const changes = op.changes as unknown as Record<string, unknown>;
  const rows = buildDiffRows(before, changes);
  const entityLabel = patchEntityLabel(op);
  return (
    <div className="space-y-2">
      {entityLabel && (
        <p className="text-2xs text-text-secondary font-medium">{entityLabel}</p>
      )}
      {rows.length === 0 ? (
        <p className="text-2xs text-text-muted italic">（沒有實際變動，AI 可能誤判 — 取消或請 AI 重新整理）</p>
      ) : (
        <div className="space-y-1">
          {rows.map((row) => (
            <div
              key={row.field}
              className={`flex flex-wrap items-baseline gap-x-1.5 text-2xs ${row.missingBefore ? "text-warning" : "text-text-secondary"}`}
            >
              <span className="font-medium">{FIELD_LABELS[row.field] ?? row.field}：</span>
              <span className="text-text-muted line-through">
                {row.missingBefore ? "（未提供現值）" : renderDiffValue(row.field, row.beforeRaw)}
              </span>
              <span className="text-text-muted">→</span>
              <span className="text-text-primary font-medium">{renderDiffValue(row.field, row.afterRaw)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function opKindLabel(kind: PlanOperation["kind"]): string {
  switch (kind) {
    case "create_task_assignment": return "建立任務派發";
    case "create_location":      return "建立地點";
    case "create_route":           return "建立路線";
    case "create_task":            return "建立任務";
    case "patch_route":            return "修改路線";
    case "patch_task":             return "修改任務";
    case "patch_task_assignment":  return "修改任務派發";
    case "patch_location":       return "修改地點";
    case "create_client":          return "建立客戶";
    case "patch_client":           return "修改客戶";
    case "create_site":            return "建立案場";
    case "patch_site":             return "修改案場";
    case "create_building":        return "建立建築物";
    case "patch_building":         return "修改建築物";
    case "create_floor":           return "建立樓層";
    case "patch_floor":            return "修改樓層";
    case "create_shift":           return "建立班別";
    case "patch_shift":            return "修改班別";
    case "create_roster":          return "新增排班";
    case "patch_roster":           return "修改排班";
    case "assign_officer_sites":   return "指派執勤者案場";
    case "create_role":            return "建立自訂角色";
    case "update_role":            return "編輯自訂角色";
    case "delete_role":            return "刪除自訂角色";
    case "assign_role_to_user":    return "指派角色給使用者";
    case "revoke_role_from_user":  return "撤銷使用者的角色";
  }
}

/** Chinese labels for diff-rendered fields. Mirrors the dashboard's own
 *  edit form labels (see Routes / Tasks / Locations / Assignments
 *  pages) so the operator sees the same vocabulary in AI-driven edits
 *  as in manual edits — DON'T invent new wording here. */
const FIELD_LABELS: Record<string, string> = {
  name:                       "名稱",
  cadence_minutes:            "頻率（分鐘）",
  expected_duration_seconds:  "預期完成時長（秒）",
  code:                       "代碼",
  label:                      "顯示名稱",
  location_hint:              "位置提示",
  risk_level:                 "風險等級",
  lat:                        "緯度",
  lon:                        "經度",
  geofence_radius_m:          "地理圍欄半徑（公尺）",
  description_short:          "簡述",
  requires_text:              "文字回報",
  requires_camera:            "現場拍照",
  requires_file:              "檔案上傳",
  verify_device:              "裝置驗證（通行鑰匙）",
  verify_face:                "人臉驗證",
  verify_geo:                 "地理位置驗證",
  requires_signature:         "需要簽名",
  allow_skip:                 "允許跳過",
  rrule:                      "時間規則 (RRULE)",
  duration_minutes:           "派發持續時間（分鐘）",
  grace_before_min:           "寬限期（前，分鐘）",
  grace_after_min:            "寬限期（後，分鐘）",
  valid_from:                 "生效時間",
  valid_to:                   "結束時間",
  target_kind:                "對象類型",
  target_value:               "對象",
  location_id:              "地點",
  route_id:                   "路線",
  status:                     "狀態",
  locations:                "包含地點",
  // Phase 3 — Client / Site / Building / Floor onboarding
  contact_name:               "聯絡人姓名",
  contact_email:              "聯絡 Email",
  contact_phone:              "聯絡電話",
  client_id:                  "所屬客戶",
  address:                    "地址",
  timezone:                   "時區",
  display_order:              "顯示順序",
  floors_count:               "地上樓層數",
  floors_below_ground_count:  "地下樓層數",
  brand_primary:              "主色 (Hex)",
  description:                "說明",
  set_as_default:             "設為預設建築物",
  floor_no:                   "樓層編號",
  floor_name:                 "樓層名稱",
  display_label:              "顯示標籤",
  is_critical:                "關鍵樓層",
  building_id:                "所屬建築物",
  site_id:                    "所屬案場",
  // Phase 4 — 班別 / 班表
  shift_id:                   "班別",
  roster_id:                  "排班記錄",
  officer_id:                 "巡邏員",
  work_date:                  "工作日期",
  start_time:                 "起始時間",
  end_time:                   "結束時間",
  days_mask:                  "適用星期",
  officer_count:              "建議人數",
  notes:                      "備註",
  // Phase 6 — Custom RBAC
  role_id:                    "角色",
  user_id:                    "使用者",
  permission_ids:             "權限清單",
  add_permission_ids:         "新增權限",
  remove_permission_ids:      "移除權限",
  cloned_from:                "複製自",
  scope:                      "範圍",
};

/** Render a value in the diff column. Booleans → 啟用/關閉, status → 中文,
 *  numbers → 字串, null/undefined → 「無」, unix-second timestamps for
 *  valid_from/to → 本地化日期。Identifier ULIDs (in patch_* changes for
 *  reparent/move cases) → 「（識別碼 結尾 XXXX）」 short form to keep
 *  the diff readable while preserving correlation against audit logs. */
function renderDiffValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return "無";
  // Inter-op $ref placeholder (spec §5.11). Shows BEFORE apply (the
  // referenced op hasn't run yet); during/after apply resolveOpRefs
  // has substituted the value with a real id string and this branch
  // is unreachable.
  if (isOpRefValue(value)) {
    return `（將在第 ${value.$op_idx + 1} 個操作完成後填入）`;
  }
  if (typeof value === "boolean") return value ? "啟用" : "關閉";
  if (field === "status") {
    if (value === "active")    return "啟用中";
    if (value === "archived")  return "已封存";
    if (value === "retired")   return "已停用";
    if (value === "inactive")  return "停用中";   // client / site / shift
    if (value === "disabled")  return "已停用";   // floor (中繼態)
    if (value === "scheduled") return "待執行";   // roster
    if (value === "confirmed") return "已確認";   // roster
    if (value === "swapped")   return "已換班";   // roster
    if (value === "cancelled") return "已取消";   // roster
  }
  if (field === "target_kind") {
    if (value === "role")    return "角色";
    if (value === "officer") return "巡邏員";
  }
  if ((field === "valid_from" || field === "valid_to") && typeof value === "number") {
    return new Date(value * 1000).toLocaleString("zh-TW", { hour12: false });
  }
  // Phase 4 — days_mask 解碼（bitmask Mon=1..Sun=64）
  if (field === "days_mask" && typeof value === "number") {
    return renderDaysMask(value);
  }
  // Phase 4 — work_date YYYY-MM-DD → 本地化 + 星期
  if (field === "work_date" && typeof value === "string") {
    return renderWorkDate(value);
  }
  // Identifier ULID short form — applies to *_id fields and to
  // target_value when it carries an officer ULID (target_kind=officer).
  if (isUlidLike(value) && (ULID_IDENTIFIER_FIELDS.has(field) || field === "target_value")) {
    return `（識別碼 結尾 ${value.slice(-4)}）`;
  }
  // Phase 6 — permission id arrays inside update_role.changes diff. Render
  // as a count; modal expand can't fit 50+ ULID-shaped ids legibly.
  if ((field === "add_permission_ids" || field === "remove_permission_ids" || field === "permission_ids") && Array.isArray(value)) {
    return `${value.length} 個權限`;
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/** Patch ops carry `before` + `changes`; render a per-field diff table.
 *  Skipped: keys where before === changes (no actual delta), keys NOT
 *  in `changes` (operator only sees fields that will move). Yellow
 *  warning row when `before[key]` is missing — AI didn't fetch the
 *  current value so the operator can't verify the diff (spec §5.5). */
interface DiffRow {
  field: string;
  beforeRaw: unknown;
  afterRaw: unknown;
  missingBefore: boolean;
}
function buildDiffRows(before: Record<string, unknown>, changes: Record<string, unknown>): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const [field, after] of Object.entries(changes)) {
    const beforeRaw = before[field];
    const missingBefore = !(field in before);
    // Skip true no-ops (before === after). Use loose equality on
    // primitives; deep compare not needed (changes shapes are flat).
    if (!missingBefore && beforeRaw === after) continue;
    rows.push({ field, beforeRaw, afterRaw: after, missingBefore });
  }
  return rows;
}

/** Phase 4 days_mask bitmask decoder (Mon=1..Sun=64). Returns zh-TW
 *  shorthand for common patterns (平日 / 週末 / 每天) or comma-separated
 *  day names otherwise. */
const DAY_LABELS = ["一", "二", "三", "四", "五", "六", "日"];
function renderDaysMask(mask: number): string {
  if (mask === 0) return "（無）";
  if (mask === 127) return "每天";
  if (mask === 31) return "週一至週五";
  if (mask === 96) return "週末";
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    if (mask & (1 << i)) out.push(DAY_LABELS[i]);
  }
  return out.join("、");
}

/** Phase 4 work_date `YYYY-MM-DD` → 「YYYY/MM/DD（X）」 with weekday. */
function renderWorkDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  // Build a Date at local-noon to dodge timezone edge cases when
  // computing the weekday (we only care about the day-of-week, not
  // the absolute instant).
  const date = new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0);
  const w = ["日", "一", "二", "三", "四", "五", "六"][date.getDay()];
  return `${y}/${mo}/${d}（${w}）`;
}

/** Modal header line shown in the OpCard's expanded view for a patch
 *  op — uses `before`'s identifier fields (always populated per spec
 *  §5.4) to give the operator a「正在修改：X」 anchor. */
function patchEntityLabel(op: PlanOperation): string | null {
  switch (op.kind) {
    case "patch_route":           return `正在修改路線：${op.before.name}`;
    case "patch_task":            return `正在修改任務：${op.before.name}`;
    case "patch_location":      return `正在修改地點：${op.before.code} ─ ${op.before.label}`;
    case "patch_task_assignment": return `正在修改派發：任務「${op.before.task_name}」於地點「${op.before.location_label}」`;
    case "patch_client":          return `正在修改客戶：${op.before.name}`;
    case "patch_site":            return `正在修改案場：${op.before.name}`;
    case "patch_building":        return `正在修改建築物：${op.before.name}`;
    case "patch_floor":           return `正在修改樓層：${op.before.floor_name}`;
    case "patch_shift":           return `正在修改班別：${op.before.name}`;
    case "patch_roster":          return `正在修改排班：${op.before.officer_name}・${renderWorkDate(op.before.work_date)}・${op.before.shift_name}`;
    case "assign_officer_sites":  return `正在指派執勤者案場：${op.before.officer_name}`;
    case "update_role":           return `正在編輯角色：${op.before.name}（目前 ${op.before.permission_count} 個權限）`;
    case "delete_role":           return `正在刪除角色：${op.before.name}（目前 ${op.before.assigned_user_count} 位使用者套用）`;
    case "assign_role_to_user":   return `將指派「${op.before.role_name}」 給 ${op.before.user_name}（目前 ${op.before.current_role_count} 個角色）`;
    case "revoke_role_from_user": return `將從 ${op.before.user_name} 撤銷「${op.before.role_name}」（撤銷後剩 ${op.before.remaining_role_count} 個角色）`;
    default: return null;
  }
}

/** Dispatch a single operation. Throws a `{ code, message, status? }`
 *  shape on failure so PlanModal can render meaningfully. Each request
 *  carries `x-plan-correlation-id` for audit grouping (spec §9.2). */
async function dispatchOperation(
  op: PlanOperation,
  planCorrelationId: string,
  tenantId: string | null,
): Promise<string | undefined> {
  // Returns the new entity id for create_* ops so caller can stash it
  // for later $ref resolution (spec §5.11). patch_* return undefined.
  //
  // We attach the correlation header via a temporary fetch wrapper —
  // each createX client function doesn't yet accept a custom header
  // bag, so we monkey-patch global headers via the api-client header
  // injection point. See `lib/api-client.ts` `__pendingPlanCorrelationId`.
  //
  // By contract this function receives `op` already deref'd by
  // resolveOpRefs (all id-typed fields are plain strings). We still
  // pass each through `idOf()` defensively — if a stray $ref reaches
  // here it surfaces as the same `unresolved_reference` error as
  // catching in the loop.
  withPlanCorrelationId(planCorrelationId);
  try {
    switch (op.kind) {
      case "create_task_assignment": {
        const r = await createAssignment(idOf(op.task_id), {
          location_id:    idOf(op.location_id),
          route_id:         idOfOrNull(op.route_id ?? null),
          rrule:            op.rrule,
          duration_minutes: op.duration_minutes ?? 60,
          grace_before_min: op.grace_before_min ?? 0,
          grace_after_min:  op.grace_after_min ?? 0,
          valid_from:       op.valid_from,
          valid_to:         op.valid_to ?? null,
          target_kind:      op.target_kind,
          target_value:     op.target_value,
        }, tenantId);
        return r.id;
      }
      case "create_location": {
        // Building / floor placement is a separate step (map_markers via
        // FloorStack editor); plan dispatcher only creates the location
        // row. Spec §5.2.
        const r = await createLocation({
          site_id:           idOf(op.site_id),
          code:              op.code,
          label:             op.label,
          risk_level:        op.risk_level,
          lat:               op.lat,
          lon:               op.lon,
          geofence_radius_m: op.geofence_radius_m,
        }, tenantId);
        return r.id;
      }
      case "create_route": {
        const r = await createRoute({
          site_id:                   idOf(op.site_id),
          name:                      op.name,
          cadence_minutes:           op.cadence_minutes,
          expected_duration_seconds: op.expected_duration_seconds,
        }, tenantId);
        const routeId = r.id;
        if (op.locations && op.locations.length > 0) {
          await putRouteLocations(
            routeId,
            op.locations.map((c, i) => ({
              location_id: idOf(c.location_id),
              sequence: i + 1,
              expected_dwell_seconds: c.expected_dwell_seconds ?? null,
            })),
            tenantId,
          );
        }
        return routeId;
      }
      // ── Phase 2 ────────────────────────────────────────────────────
      case "create_task": {
        const r = await createTask({
          name:               op.name,
          description_short:  op.description_short,
          requires_text:      op.requires_text,
          requires_camera:    op.requires_camera,
          requires_file:      op.requires_file,
          verify_device:      op.verify_device,
          verify_face:        op.verify_face,
          verify_geo:         op.verify_geo,
          requires_signature: op.requires_signature,
          allow_skip:         op.allow_skip,
        }, tenantId);
        return r.id;
      }
      case "patch_route": {
        await patchRoute(idOf(op.route_id), op.changes as Parameters<typeof patchRoute>[1], tenantId);
        return undefined;
      }
      case "patch_task": {
        await patchTask(idOf(op.task_id), op.changes as Parameters<typeof patchTask>[1], tenantId);
        return undefined;
      }
      case "patch_task_assignment": {
        await patchAssignment(idOf(op.assignment_id), op.changes as Parameters<typeof patchAssignment>[1], tenantId);
        return undefined;
      }
      case "patch_location": {
        await patchLocation(idOf(op.location_id), op.changes, tenantId);
        return undefined;
      }
      // ── Phase 3: Client / Site / Building / Floor onboarding ───────
      case "create_client": {
        const r = await createClient({
          name:          op.name,
          contact_name:  op.contact_name,
          contact_email: op.contact_email,
          contact_phone: op.contact_phone,
          status:        op.status,
        }, tenantId);
        return r.id;
      }
      case "patch_client": {
        await patchClient(idOf(op.client_id), op.changes, tenantId);
        return undefined;
      }
      case "create_site": {
        const r = await createSite({
          client_id:  idOf(op.client_id),
          name:       op.name,
          address:    op.address,
          timezone:   op.timezone,
          risk_level: op.risk_level,
          status:     op.status,
        }, tenantId);
        return r.id;
      }
      case "patch_site": {
        await patchSite(idOf(op.site_id), op.changes as Parameters<typeof patchSite>[1], tenantId);
        return undefined;
      }
      case "create_building": {
        const r = await createBuilding({
          site_id:                   idOf(op.site_id),
          name:                      op.name,
          display_order:             op.display_order,
          floors_count:              op.floors_count,
          floors_below_ground_count: op.floors_below_ground_count,
          brand_primary:             op.brand_primary,
          description:               op.description,
          set_as_default:            op.set_as_default,
        }, tenantId);
        return r.id;
      }
      case "patch_building": {
        await patchBuilding(idOf(op.building_id), op.changes, tenantId);
        return undefined;
      }
      case "create_floor": {
        const r = await createFloor({
          building_id:    idOf(op.building_id),
          floor_no:       op.floor_no,
          floor_name:     op.floor_name,
          display_label:  op.display_label,
          display_order:  op.display_order,
          is_critical:    op.is_critical,
        }, tenantId);
        return r.id;
      }
      case "patch_floor": {
        await patchFloor(idOf(op.floor_id), op.changes, tenantId);
        return undefined;
      }
      // ── Phase 4: 班別 / 班表 (schedule) ────────────────────────────
      case "create_shift": {
        const r = await createShift({
          name:           op.name,
          start_time:     op.start_time,
          end_time:       op.end_time,
          days_mask:      op.days_mask,
          officer_count:  op.officer_count ?? 1,
          status:         op.status,
        }, tenantId);
        return r.id;
      }
      case "patch_shift": {
        await patchShift(idOf(op.shift_id), op.changes, tenantId);
        return undefined;
      }
      case "create_roster": {
        const r = await createRoster({
          shift_id:   idOf(op.shift_id),
          officer_id: idOf(op.officer_id),
          site_id:    idOf(op.site_id),
          work_date:  op.work_date,
          notes:      op.notes,
          status:     op.status,
        }, tenantId);
        return r.id;
      }
      case "patch_roster": {
        await patchRoster(idOf(op.roster_id), op.changes as Parameters<typeof patchRoster>[1], tenantId);
        return undefined;
      }
      case "assign_officer_sites": {
        await putOfficerSites(idOf(op.officer_id), op.site_ids.map(idOf), tenantId);
        return undefined;
      }
      // ── Phase 6: Custom RBAC role + assignment ops ─────────────────
      case "create_role": {
        const r = await createRole({
          name: op.name,
          description: op.description ?? null,
          permission_ids: op.permission_ids,
          cloned_from: op.cloned_from ?? null,
        }, tenantId);
        // Some backends apply permission_ids during create, some require a
        // follow-up PUT. The PUT is idempotent so we always send it when the
        // operator picked any permissions.
        if (op.permission_ids.length > 0) {
          try {
            await replaceRolePermissions(r.id, op.permission_ids, tenantId);
          } catch {
            // Best-effort — create succeeded; editor can fix on detail page.
          }
        }
        return r.id;
      }
      case "update_role": {
        const roleId = idOf(op.role_id);
        const changes = op.changes;
        // Metadata patch (name / description) — skip when neither changed.
        if (changes.name !== undefined || changes.description !== undefined) {
          await updateRole(roleId, {
            name: changes.name,
            description: changes.description,
          }, tenantId);
        }
        // Permission diff — server has PUT replace semantics so we fetch
        // current set, apply add/remove, then PUT the result.
        const addList = changes.add_permission_ids ?? [];
        const removeList = changes.remove_permission_ids ?? [];
        if (addList.length > 0 || removeList.length > 0) {
          const detail = await getRole(roleId, tenantId);
          const next = new Set(detail.permissions);
          for (const id of removeList) next.delete(id);
          for (const id of addList) next.add(id);
          await replaceRolePermissions(roleId, [...next], tenantId);
        }
        return undefined;
      }
      case "delete_role": {
        await deleteRole(idOf(op.role_id), tenantId);
        return undefined;
      }
      case "assign_role_to_user": {
        const userId = op.user_id;
        const roleId = idOf(op.role_id);
        const existing = await getUserRoles(userId, tenantId);
        const next = [
          ...existing.items.map((a) => ({ role_id: a.role_id, scope_json: a.scope_json })),
          { role_id: roleId, scope_json: op.scope ?? null },
        ];
        await putUserRoles(userId, next, tenantId);
        return undefined;
      }
      case "revoke_role_from_user": {
        const userId = op.user_id;
        const roleId = idOf(op.role_id);
        const existing = await getUserRoles(userId, tenantId);
        const next = existing.items
          .filter((a) => a.role_id !== roleId)
          .map((a) => ({ role_id: a.role_id, scope_json: a.scope_json }));
        await putUserRoles(userId, next, tenantId);
        return undefined;
      }
    }
  } finally {
    clearPlanCorrelationId();
  }
}

// ── Header carrier ─────────────────────────────────────────────────
//
// `apiFetch` reads this single global. While set, every outbound
// request gets `x-plan-correlation-id: <id>`. Cleared in the
// dispatchOperation's finally block so unrelated requests after the
// dispatch don't get the header. Single-threaded by virtue of being
// JS — only one plan applying at a time per browser tab (spec §18
// risk row: "同個 user 兩個 tab 各跑 plan" — UI prevents this by
// only allowing one PlanModal mounted at a time).

declare global {
  // eslint-disable-next-line no-var
  var __pendingPlanCorrelationId: string | undefined;
}

function withPlanCorrelationId(id: string): void {
  globalThis.__pendingPlanCorrelationId = id;
}

function clearPlanCorrelationId(): void {
  globalThis.__pendingPlanCorrelationId = undefined;
}
